import { useEffect, useReducer, useRef, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { CalendarDays, Check, ChevronDown, ChevronUp, ClipboardList, FileText, Layers, Loader2, ShieldCheck, Sparkles, Target } from "lucide-react";
import {
  SKILL_FOCUS_OPTIONS,
  GRADE_OPTIONS,
  SUBJECT_OPTIONS,
  TIME_OPTIONS,
  ACTIVITY_TYPE_OPTIONS,
} from "../lib/options";
import {
  getGradesFromCurriculum,
  getSpecificsForGradeSubject,
  getStrandsForGradeSubject,
  getSubjectsForGrade,
  isCombinedGrade,
  useCurriculum,
} from "../lib/curriculum";
import { UnitStep1 } from "./UnitStep1";
import { INITIAL_FORM_STATE, formReducer } from "../lib/generatorFormReducer";
import CrossCurricularPicker from "./CrossCurricularPicker";
import CombinedGradeColumns from "./CombinedGradeColumns";
import { SkillFocusPicker } from "./SkillFocusPicker";
import LessonGroundingPicker from "./LessonGroundingPicker";

const fieldClass =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md h-12";

// Step 8 — map a skill_focus display name to its store skill_id, using the
// same tolerant match the skill-only form uses (exact skill_name, then a
// first-word contains fallback). Returns null when it can't resolve.
function resolveSkillId(focusStore, title) {
  if (!focusStore || !title) return null;
  const t = title.trim().toLowerCase();
  for (const [sid, s] of Object.entries(focusStore)) {
    if ((s.skill_name || "").toLowerCase() === t) return sid;
  }
  for (const [sid, s] of Object.entries(focusStore)) {
    const first = (s.skill_name || "").toLowerCase().split(/[ ,&]/)[0];
    if (first && t.includes(first)) return sid;
  }
  return null;
}

const LESSON_COUNT_OPTIONS = ["3", "5", "7", "10", "12"];  // retained: legacy option list (unit uses a free-text field now)
// Phase 85a — Summative Task class-period count. Backend enforces 2-10.
const PERIOD_COUNT_OPTIONS = ["2", "3", "4", "5", "6", "8", "10"];
const SUMMATIVE_FORMAT_OPTIONS = [
  "Performance Task",
  "Project / Product",
  "Inquiry Investigation",
  "Portfolio",
  "Presentation",
  "Written Response",
];
const UNIT_FORMAT_OPTIONS = [
  "Project-Based",
  "Inquiry / Discovery",
  "Hands-On / Maker",
  "Collaborative / Team",
  "Skills Practice",
  "Performance / Showcase",
  "Cross-Curricular",
];

// Optional sections the teacher can include or omit. Required sections
// (curriculum, skill focus, look-fors, UDL) are not in this list — they
// always appear in every generated lesson.
const OPTIONAL_SECTIONS = [
  { key: "differentiation", label: "Differentiation", hint: "Pathways for IEPs, ELLs, extensions" },
  { key: "extension", label: "Extension", hint: "What students who finish early can dig into" },
  { key: "exit_ticket", label: "Exit ticket", hint: "Quick end-of-lesson check for understanding" },
  { key: "student_worksheets", label: "Student worksheets", hint: "Printable handouts paced with the lesson" },
];

const ALWAYS_INCLUDED = "Learning goals · Success criteria · Curriculum expectations · Skill focus · Look-fors · UDL pathways · Report card language (E/G/S/N)";

// The reducer + initial state for this form live in
// `../lib/generatorFormReducer.js` so they can be unit-tested without
// pulling in the React UI tree. See that file for the full action list.

export const GeneratorForm = ({ onGenerate, loading, onPlanInputsChange, hideUnitToggle = false }) => {
  // Default = everything optional included. Teachers can untick what they don't
  // want. Per the user's preference these are NOT persisted — every visit
  // starts fresh so it's a conscious choice each time.
  const [includeSections, setIncludeSections] = useState(
    () => new Set(OPTIONAL_SECTIONS.map((s) => s.key))
  );
  const [sectionsOpen, setSectionsOpen] = useState(true);
  // Phase 92 — opt-in Community sharing. Both default to off; the
  // teacher actively ticks the checkbox to share. Display name is
  // free-form and capped server-side at 60 chars.
  const [shareToCommunity, setShareToCommunity] = useState(false);
  const [communityDisplayName, setCommunityDisplayName] = useState("");
  // Phase 128 — Summative Task Designer: when ON, the LLM emits three
  // labelled student-sheet variants per supporting material (support /
  // on-grade / extension). Default OFF — on-grade sheets only.
  const [includeDifferentiated, setIncludeDifferentiated] = useState(false);
  const { data: curriculum } = useCurriculum();

  // All cascading state (mode + form fields + summative checklist + skill
  // subcomponents) lives in one reducer — see formReducer above. We pull
  // the slices we read into locals so the existing JSX needs no rewrite.
  const [state, dispatch] = useReducer(formReducer, INITIAL_FORM_STATE);
  const { mode, form, summativeExpectations, subjects, strands, expectations } = state;
  // Step 8 — store-driven focus-area picker state (shared endpoint with the
  // skill-only form). focusStore = GET /api/skills/focus-areas; lookforNums =
  // the up-to-two look-for numbers the teacher picked for form.skill_focus.
  const [focusStore, setFocusStore] = useState(null);
  const [lookforNums, setLookforNums] = useState([]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/skills/focus-areas`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setFocusStore)
      .catch(() => setFocusStore(null));
  }, []);
  // Reset look-for picks whenever the chosen skill changes.
  useEffect(() => {
    setLookforNums([]);
  }, [form.skill_focus]);
  // 1b Stage B — unit-mode Step 1 local state (isolated so lesson/summative
  // mode is byte-identical). The confirmed big idea + its source overall
  // codes ride to the backend as `big_idea` (verbatim) + source codes; the
  // multi-select skills ride as `transferable_skills[]`.
  const [bigIdea, setBigIdea] = useState({ text: "", sourceCodes: [], suggestedCodes: [], source: null });
  const [unitSkills, setUnitSkills] = useState([]);
  const SKILL_AUTO_PICK = "Let Atomic Edu choose";
  // units-on-the-store — per-skill focus-area picks for a UNIT ({skill_id:[nums]})
  // and, for a split-grade unit, the teacher's chosen look-for grade. Both feed
  // the proposal engine + the overview-card pickers.
  const [unitFocusAreas, setUnitFocusAreas] = useState({});
  const [lookforGrade, setLookforGrade] = useState("");
  // Distinct grades 4-8 named by the current grade string ("5/6" -> ["5","6"]).
  const splitGrades = Array.from(
    new Set((String(form.grade || "").match(/[4-8]/g) || []))
  );
  const isSplitGrade = splitGrades.length > 1;
  const isCombined = isCombinedGrade(form.grade);
  // Drop focus-area picks + look-for grade when the inputs that scope them change.
  useEffect(() => {
    setUnitFocusAreas((prev) => {
      const ids = new Set(unitSkills.map((n) => resolveSkillId(focusStore, n)).filter(Boolean));
      const next = {};
      Object.entries(prev).forEach(([sid, nums]) => { if (ids.has(sid)) next[sid] = nums; });
      return next;
    });
  }, [unitSkills, focusStore]);
  // Combined Grades (lesson mode) — per-grade column subject + mirror link.
  // Reset when the grade changes so a fresh split grade starts clean/linked.
  const [colSubject, setColSubject] = useState({});
  const [gradeMirrorLinked, setGradeMirrorLinked] = useState(true);
  useEffect(() => {
    setLookforGrade("");
    setColSubject({});
    setGradeMirrorLinked(true);
  }, [form.grade]);
  // Fork Relocation (Stage B) — unit Step 2 assessment model. When the
  // teacher picks "standalone" up front we run the STAGED build (draft the
  // culminating task first, then generate task-aware lessons). "decide_at_end"
  // (default) keeps today's behaviour: a plain unit, choose assessment at the
  // bottom of the finished unit. Distributed is intentionally not offered here
  // yet — its per-lesson component registration ships in a later lane.
  const [assessmentModel, setAssessmentModel] = useState("decide_at_end");

  const isUnit = mode === "unit";
  const isSummative = mode === "summative";
  // Combined Grades two-column selection surface applies to LESSON mode only.
  const isCombinedLesson = isCombined && !isUnit && !isSummative;

  // Phase 119.A/D — taught-lesson grounding. When the teacher reaches
  // this form via the unit's "Build a summative" bridge (sessionStorage
  // key `atomicedu_pending_unit_summative_attach`), we pre-attach the
  // parent unit's already-generated child lessons. The picker is
  // collapsed by default with an "Attach more from library" toggle.
  const [attachedLessonIds, setAttachedLessonIds] = useState([]);
  const [autoAttachedCount, setAutoAttachedCount] = useState(0);
  useEffect(() => {
    if (!isSummative) return;
    let cancelled = false;
    (async () => {
      let pendingUnitId = "";
      try {
        const raw = sessionStorage.getItem("atomicedu_pending_unit_summative_attach");
        if (raw) {
          const parsed = JSON.parse(raw);
          pendingUnitId = parsed?.unit_activity_id || "";
        }
      } catch (_) { /* swallow */ }
      if (!pendingUnitId) return;
      try {
        const { listUnitLessons } = await import("../lib/api");
        const kids = await listUnitLessons(pendingUnitId);
        if (cancelled) return;
        const ids = (kids || []).map((c) => c.id).filter(Boolean);
        if (ids.length > 0) {
          setAttachedLessonIds(ids);
          setAutoAttachedCount(ids.length);
        }
      } catch (_e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [isSummative]);

  // Phase 119.E series-builder — when the Generator is launched from
  // a lesson's "Write next →" CTA (`?ground_lesson_id=…`), pre-attach
  // that lesson AND pre-fill grade/subject/strand so the teacher can
  // immediately write a follow-up. Uses the atomic APPLY_URL_PREFILL
  // reducer action so we don't race with RECONCILE_AGAINST_CURRICULUM.
  const groundPrefillAppliedRef = useRef(false);
  useEffect(() => {
    if (groundPrefillAppliedRef.current) return;
    if (!curriculum) return;  // Wait for curriculum before reconciling
    let groundId = "";
    try {
      const params = new URLSearchParams(window.location.search);
      groundId = params.get("ground_lesson_id") || "";
    } catch (_) { /* ignore */ }
    if (!groundId) return;
    let cancelled = false;
    (async () => {
      try {
        const { getActivity } = await import("../lib/api");
        const lesson = await getActivity(groundId);
        if (cancelled || !lesson) return;
        setAttachedLessonIds((prev) => Array.from(new Set([...(prev || []), groundId])));
        setAutoAttachedCount((n) => n + 1);
        const inputs = lesson.inputs || {};
        let g = lesson.grade || inputs.grade || "";
        const s = lesson.subject || inputs.subject || "";
        const st = lesson.strand || inputs.strand || "";
        // Coerce bare numeric grade ("5") to curriculum format ("Grade 5")
        // when needed — older lessons stored a bare digit in inputs.grade.
        if (g && !/^Grade\s/i.test(g) && curriculum?.grades) {
          const match = curriculum.grades.find(
            (cg) => cg === g || cg === `Grade ${g}` || cg.endsWith(` ${g}`),
          );
          if (match) g = match;
        }
        // Atomic: set grade+subject+strand together AFTER reconcile.
        dispatch({
          type: "APPLY_URL_PREFILL",
          mode: lesson.output_type === "unit" ? "unit" : "lesson",
          form: { grade: g, subject: s, strand: st },
          expectations: [],
          curriculum,
        });
        groundPrefillAppliedRef.current = true;
      } catch (_e) { /* non-fatal — getActivity 403/404 from cross-teacher safety */ }
    })();
    return () => { cancelled = true; };
  }, [curriculum]);
  // Sentinel value for the "clear this field" option that sits at the top
  // of every dropdown. Shadcn SelectItem cannot have an empty-string value,
  // so we use this token and translate it back to "" in setField.
  const CLEAR = "__clear__";
  const setField = (key) => (val) => {
    const next = val === CLEAR ? "" : val;
    dispatch({ type: "SET_FIELD", key, value: next, curriculum });
  };
  const setMode = (m) => dispatch({ type: "SET_MODE", mode: m });

  const baseGradeOptions = curriculum
    ? getGradesFromCurriculum(curriculum)
    : GRADE_OPTIONS;
  // Combined-grade options (4/5, 5/6, 6/7, 7/8) — interleaved right after
  // their lower base grade so they're obvious in the dropdown, and only
  // offered when both component grades exist in the curriculum.
  const _combinedByLower = {
    "Grade 4": "Grade 4/5",
    "Grade 5": "Grade 5/6",
    "Grade 6": "Grade 6/7",
    "Grade 7": "Grade 7/8",
  };
  const gradeOptions = [];
  for (const g of baseGradeOptions) {
    gradeOptions.push(g);
    const cg = _combinedByLower[g];
    if (cg) {
      const [a, b] = cg.match(/[4-8]/g);
      if (baseGradeOptions.includes(`Grade ${a}`) && baseGradeOptions.includes(`Grade ${b}`)) {
        gradeOptions.push(cg);
      }
    }
  }
  const subjectOptions = form.grade && curriculum
    ? getSubjectsForGrade(curriculum, form.grade)
    : SUBJECT_OPTIONS;
  const strandOptions = curriculum
    ? getStrandsForGradeSubject(curriculum, form.grade, form.subject)
    : [];
  // Full specific-expectation rows for the (grade, subject) — empty for
  // uncurated subjects (kept for legacy library compatibility). Each row is shaped
  // { code, topic, description }.
  const allSpecifics = curriculum
    ? getSpecificsForGradeSubject(curriculum, form.grade, form.subject)
    : [];
  // Codes filtered by the chosen strand letter (e.g. strand "C" → C1.1, C2.4…).
  const specificOptions = form.strand
    ? allSpecifics.filter((row) => row.code && row.code.startsWith(form.strand))
    : [];
  const hasCuratedSpecifics = specificOptions.length > 0;

  // Phase 90 — URL prefill, used by the Math Engine "End-of-Unit
  // Assignment" CTA so a teacher lands here with the right mode +
  // grade + subject + expectations already selected. Read once on
  // first render, deferred-apply once curriculum has loaded so the
  // reconciler can validate strand codes against the real data.
  const urlPrefillRef = useRef(null);
  if (urlPrefillRef.current === null) {
    try {
      const params = new URLSearchParams(window.location.search);
      const m = params.get("mode");
      const g = params.get("grade");
      const subj = params.get("subject");
      let strandCode = params.get("strand");
      const expRaw = params.get("expectations");
      const expectations = expRaw
        ? expRaw.split(",").map((c) => c.trim()).filter(Boolean)
        : [];
      // Derive strand from the first expectation code when it isn't
      // passed explicitly — e.g. "B1.1,B1.2" → strand "B". This keeps
      // the strand dropdown pinned so the multi-expectation checklist
      // actually renders.
      if (!strandCode && expectations.length > 0) {
        const first = expectations[0];
        if (first && /^[A-Z]/.test(first)) strandCode = first[0];
      }
      const hasAny = !!(m || g || subj || strandCode || expectations.length);
      urlPrefillRef.current = hasAny
        ? {
            mode: m,
            form: {
              ...(g ? { grade: g } : {}),
              ...(subj ? { subject: subj } : {}),
              ...(strandCode ? { strand: strandCode } : {}),
            },
            expectations,
          }
        : false;
    } catch (_e) {
      urlPrefillRef.current = false;
    }
  }
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    if (!urlPrefillRef.current || !curriculum) return;
    dispatch({
      type: "APPLY_URL_PREFILL",
      ...urlPrefillRef.current,
      curriculum,
    });
    prefillAppliedRef.current = true;
    urlPrefillRef.current = false;
  }, [curriculum]);

  // Reconcile any pre-set values against the real curriculum once it
  // loads. No-op when there are no stale values to drop.
  useEffect(() => {
    if (!curriculum) return;
    dispatch({ type: "RECONCILE_AGAINST_CURRICULUM", curriculum });
  }, [curriculum]);

  // sessionStorage prefill — UnitDocument's "Use above" button writes
  // here before scrolling to the generator. We pick it up on mount AND
  // on tab refocus (covers click-then-scroll race conditions).
  useEffect(() => {
    const pickUp = () => {
      try {
        const seeded = sessionStorage.getItem("atomicedu_prefill_prompt");
        const seededSkill = sessionStorage.getItem("atomicedu_prefill_skill_focus");
        if (!seeded && !seededSkill) return;
        dispatch({
          type: "APPLY_SESSION_PREFILL",
          prompt: seeded || "",
          skillFocus: seededSkill || "",
        });
        if (seeded) sessionStorage.removeItem("atomicedu_prefill_prompt");
        if (seededSkill) sessionStorage.removeItem("atomicedu_prefill_skill_focus");
      } catch (_e) {
        /* sessionStorage blocked */
      }
    };
    pickUp();
    window.addEventListener("focus", pickUp);
    return () => window.removeEventListener("focus", pickUp);
  }, []);

  // Available sub-components for the currently selected skill. Empty
  // array means the chosen skill has no curated checklist (e.g.
  // Self-Regulation, which is a Learning Skill, not one of the 7).
  // Step 8 (Jun 2026) — the graded generator's focus-area picker reads the
  // SAME verified look-for store as the skill-only form, via
  // GET /api/skills/focus-areas. Labels are the store's lookfor_name, the
  // line under each is use_when, and the list length follows the skill. The
  // teacher's picks submit as `skill_lookfor_selections` (look-for NUMBERS,
  // never labels), capped at two, driving the deterministic look-for / WALT /
  // SC / exit-ticket paths. No focus-area wording is hardcoded here.
  const skillFocusId = resolveSkillId(focusStore, form.skill_focus);
  const focusAreas =
    focusStore && skillFocusId ? focusStore[skillFocusId].lookfors || [] : [];
  const toggleLookfor = (num) =>
    setLookforNums((prev) =>
      prev.includes(num)
        ? prev.filter((n) => n !== num)
        : prev.length >= 2
        ? prev
        : [...prev, num]
    );
  const skillLookforSelections = skillFocusId
    ? lookforNums.map((n) => ({ skill_id: skillFocusId, lookfor_number: n }))
    : [];
  const toggleSummativeExpectation = (code) =>
    dispatch({ type: "TOGGLE_SUMMATIVE_EXPECTATION", code });

  // Phase 96 — multi-pick cap per mode. Lessons: 4. Summative: 8.
  // Unit: scales with the number of lessons (2 per lesson, bounded
  // between 4 and 12) so a 3-lesson unit stays focused while a 10-
  // lesson unit can sweep wider.
  const numLessonsInt = Math.max(1, parseInt(form.num_lessons || "5", 10) || 5);
  // Base cap per mode; for a combined grade multiply by the number of
  // component grades so a teacher can cover each grade's expectations
  // (e.g. Social Studies for Gr 6 + History/Geography for Gr 7).
  const _gradeSpan = Math.max(1, splitGrades.length);
  const expectationCap = (isSummative
    ? 8
    : isUnit
    ? Math.min(12, Math.max(4, numLessonsInt * 2))
    : 4) * _gradeSpan;

  const hasPrompt = (form.prompt || "").trim().length > 0;
  // Tier 2 #4 — skill_focus is now REQUIRED on lesson/unit modes. The
  // teacher must actively pick one of the 7 transferable skills OR
  // choose "Let Atomic Edu choose" — both are valid non-empty values.
  // Grade + Subject also required. Summative has its own readiness
  // check below.
  const hasMinDropdowns = !!form.grade && !!form.skill_focus && !!form.subject;
  // Summative tasks need a strand AND at least 2 expectations — the
  // backend enforces this too but we mirror it here so the Generate
  // button can disable cleanly and we can show an inline hint.
  const summativeReady =
    !!form.grade &&
    !!form.subject &&
    !!form.strand &&
    hasCuratedSpecifics &&
    summativeExpectations.length >= 2;
  // Reading Generator gate — if the teacher ticked "Include Reading"
  // they must pick a text type AND (for prose types) at least one
  // reading level before Generate enables. Structural types (Comic
  // Strip / Primary Source) are Anchor-only so the level list is
  // implicitly satisfied.
  const _isStructuralReading = ["comic_strip", "primary_source"].includes(
    form.reading_text_type,
  );
  const _selectedReadingModes = _isStructuralReading
    ? ["anchor"]
    : (form.reading_modes || []);
  const readingReady =
    !form.include_reading ||
    (!!form.reading_text_type && _selectedReadingModes.length >= 1);
  // 1b Stage B — unit readiness: grade + subject + >=1 strand satisfying the
  // childless rule + a big idea + >=1 specific expectation. Skills optional
  // (fallback). Supersedes the skill_focus-first requirement in unit mode.
  const unitChildlessLetters =
    (curriculum?.childless_strands?.[form.grade]?.[form.subject]) || [];
  const unitStrandCodes = strands
    .filter((s) => s.subject === form.subject)
    .map((s) => s.code);
  const unitStrandsOk =
    unitStrandCodes.length > 0 &&
    unitStrandCodes.some((l) => !unitChildlessLetters.includes(l));
  const unitSpecCount = expectations.filter((e) => e.subject === form.subject).length;
  // units-on-the-store gates: every SELECTED skill must have >=1 focus area
  // chosen (so the proposal can carry a look-for), and a split-grade unit must
  // have its look-for grade chosen — no silent default.
  // Step 16 defect 1: a unit MUST carry one or two transferable skills, each
  // with >=1 focus area. (Previously `unitSkills.length === 0 ||` let a unit
  // generate with NO skill — nothing for the proposal engine to work from.)
  const unitSkillsValid =
    unitSkills.length >= 1 && unitSkills.length <= 2 &&
    unitSkills.every((name) => {
      const sid = resolveSkillId(focusStore, name);
      return sid && (unitFocusAreas[sid] || []).length > 0;
    });
  const unitLookforGradeOk = !isSplitGrade || !!lookforGrade;
  const unitReady =
    !!form.grade && !!form.subject && unitStrandsOk &&
    (bigIdea.text || "").trim().length > 0 && unitSpecCount >= 1 &&
    unitSkillsValid && unitLookforGradeOk;
  // Unit lesson-count is a free-text field (the entered number = CONTENT
  // lessons; the unit adds a launch lesson on top). Validate 4–12 with an
  // explicit message — never silently clamp.
  const _rawLessons = (form.num_lessons ?? "").toString().trim();
  let unitLessonError = "";
  if (isUnit) {
    if (_rawLessons === "") unitLessonError = "Enter how many content lessons you want (4–12).";
    else if (!/^\d+$/.test(_rawLessons)) unitLessonError = "Enter a whole number between 4 and 12.";
    else {
      const _v = parseInt(_rawLessons, 10);
      if (_v < 4) unitLessonError = "Enter at least 4 — fewer than that isn't enough sequence to build toward a culminating task.";
      else if (_v > 12) unitLessonError = "Enter 12 or fewer — more than that is really two units.";
    }
  }
  const unitLessonCountValid = !isUnit || !unitLessonError;
  // For a split-grade LESSON the teacher must pick which grade's look-fors
  // to use (mirrors the unit-mode requirement — no silent default).
  const lessonSplitGradeOk = isUnit || !isSplitGrade || !!lookforGrade;
  // Combined-grade expectation-picker rule (Jun 2026): a split-grade lesson
  // built in the picker must carry >=1 expectation from EACH grade — Atomic
  // Edu never fills the empty grade in. Free-prompt / resource lessons carry
  // no picker and are exempt (this only gates the combined-columns flow).
  const combinedMissingGrades = isCombinedLesson
    ? splitGrades.filter(
        (n) => !expectations.some((e) => (String(e.grade || "").match(/[4-8]/g) || []).includes(n))
      )
    : [];
  const combinedGradeExpectationsOk = !isCombinedLesson || combinedMissingGrades.length === 0;
  const ready =
    (isSummative ? summativeReady : isUnit ? (unitReady && unitLessonCountValid) : ((hasPrompt || hasMinDropdowns) && lessonSplitGradeOk && combinedGradeExpectationsOk)) &&
    readingReady;

  // Emit a fingerprint of the PLAN-relevant inputs whenever they change, so the
  // parent can discard a stale Coverage Plan if the teacher edits selections
  // after (or during) the ~60s pre-call — same guard class as the big-idea fetch.
  useEffect(() => {
    if (typeof onPlanInputsChange !== "function") return;
    const fp = JSON.stringify({
      g: form.grade || "",
      s: form.subject || "",
      st: form.strand || "",
      e: expectations.map((x) => x.code).sort(),
      b: (bigIdea.text || "").trim(),
      m: assessmentModel,
      n: form.num_lessons || "",
    });
    onPlanInputsChange(fp);
  }, [
    form.grade, form.subject, form.strand, form.num_lessons,
    expectations, bigIdea.text, assessmentModel, onPlanInputsChange,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!ready || loading) return;
    // Compose a readable strand label so it lands cleanly in the saved
    // activity inputs + in Claude's user message (e.g. "C — Algebra").
    let strandLabel = "";
    if (form.strand) {
      const match = strandOptions.find((s) => s.code === form.strand);
      strandLabel = match ? `${match.code} — ${match.name}` : form.strand;
    }
    // Phase 96 — pull canonical multi-pick arrays. Always send them
    // (backend treats them as additive coverage on top of the legacy
    // single-pick fields). When the teacher used only the single-pick
    // dropdowns, `subjects` will be [primary], `strands` will be
    // [{subject:primary, code:strand}], and `expectations` will hold
    // whatever they ticked in the picker (or in summative mode the
    // existing summativeExpectations array).
    const multi_subjects = subjects.slice();
    const multi_strands = strands.map(({ subject, code }) => {
      const list = curriculum
        ? getStrandsForGradeSubject(curriculum, form.grade, subject)
        : [];
      const match = list.find((s) => s.code === code);
      return {
        subject,
        code,
        label: match ? `${match.code} — ${match.name}` : code,
      };
    });
    const multi_expectations = expectations.map((e) => ({
      subject: e.subject,
      strand: e.strand,
      code: e.code,
      ...(e.grade ? { grade: e.grade } : {}),
    }));
    // Phase 85a — summative tasks ride a different endpoint and need
    // an `expectations` array (plus a 2-10 num_lessons "periods" count).
    if (isSummative) {
      onGenerate({
        ...form,
        strand: strandLabel,
        expectation: "",
        output_type: "summative_task",
        num_lessons: parseInt(form.num_lessons || "5", 10),
        // Phase 128 — propagate the differentiation toggle.
        include_differentiated_sheets: includeDifferentiated,
        // Use canonical multi-pick when it has entries, else fall back
        // to the legacy summative checklist. Codes-only flat array for
        // backwards compat with the existing backend validator.
        expectations:
          multi_expectations.length > 0
            ? multi_expectations.map((e) => e.code)
            : summativeExpectations,
        subjects: multi_subjects,
        strands_detail: multi_strands,
        expectations_detail: multi_expectations,
        include_sections: Array.from(includeSections),
        skill_lookfor_selections: skillLookforSelections,
        share_to_community: shareToCommunity,
        community_display_name: shareToCommunity ? communityDisplayName.trim() : "",
        // Phase 119.A/D — taught-lesson grounding. Pre-attached from
        // the parent unit when launched in-unit; otherwise the
        // teacher's manual picks.
        attached_lesson_ids: attachedLessonIds,
      });
      return;
    }
    const payload = {
      ...form,
      strand: strandLabel,
      output_type: isUnit ? "unit" : "lesson",
      num_lessons: isUnit ? parseInt(form.num_lessons || "5", 10) : undefined,
      // 1b Stage B — unit-mode Step 1. Verbatim big idea + its source
      // overall codes; multi-select transferable skills. skill_focus is
      // set to the joined selection (no primary-skill hierarchy) or the
      // auto-pick sentinel so the backend can choose 1-2 when none picked.
      ...(isUnit
        ? {
            big_idea: (bigIdea.text || "").trim(),
            big_idea_source_overall_codes: bigIdea.sourceCodes || [],
            big_idea_source: bigIdea.source || null,
            transferable_skills: unitSkills,
            skill_focus: unitSkills.length ? unitSkills.join(", ") : SKILL_AUTO_PICK,
            // units-on-the-store — closed per-skill focus-area sets + split-grade
            // look-for choice. Both paths (plain + staged) get these because the
            // staged payload spreads this same object.
            unit_focus_areas: Object.entries(unitFocusAreas)
              .map(([sid, nums]) => ({ skill_id: sid, lookfor_numbers: nums }))
              .filter((x) => (x.lookfor_numbers || []).length > 0),
            lookfor_grade: isSplitGrade ? lookforGrade : "",
          }
        : {}),
      // For lesson/unit, send canonical arrays alongside the legacy
      // single-pick fields. Backend uses them as primary coverage when
      // multiple expectations are present.
      subjects: multi_subjects,
      strands_detail: multi_strands,
      expectations:
        multi_expectations.length > 0
          ? multi_expectations.map((e) => e.code)
          : undefined,
      expectations_detail: multi_expectations,
      // Split-grade look-for choice for lessons (units set this in their
      // own branch above). Empty string when not a split grade.
      lookfor_grade: isSplitGrade ? lookforGrade : "",
      include_sections: Array.from(includeSections),
      skill_lookfor_selections: skillLookforSelections,
      share_to_community: shareToCommunity,
      community_display_name: shareToCommunity ? communityDisplayName.trim() : "",
      // Reading Generator — only meaningful when output_type === "lesson".
      include_reading: !isUnit && !!form.include_reading && !!form.reading_text_type,
      reading_text_type: !isUnit && form.include_reading ? form.reading_text_type : "",
      // Jun 2026 — multi-level readings. Send the selected levels (1, 2,
      // or all 3). Structural text types (Comic Strip / Primary Source)
      // are Anchor-only. `reading_mode` stays as the primary level for
      // backward compatibility with older callers.
      reading_modes:
        !isUnit && form.include_reading && form.reading_text_type
          ? (["comic_strip", "primary_source"].includes(form.reading_text_type)
              ? ["anchor"]
              : (form.reading_modes && form.reading_modes.length
                  ? form.reading_modes
                  : ["anchor"]))
          : [],
      reading_mode:
        !isUnit &&
        form.include_reading &&
        !["comic_strip", "primary_source"].includes(form.reading_text_type)
          ? (form.reading_modes && form.reading_modes.length
              ? form.reading_modes[0]
              : "anchor")
          : "anchor",
      // Phase 119.E — taught-lesson grounding for lesson + unit modes
      // (extends the same mechanism used for summatives).
      attached_lesson_ids: attachedLessonIds,
    };
    // Fork Relocation (Stage B) — when a unit teacher chose a summative
    // model up front, route to the STAGED build (task → task-aware
    // lessons) instead of the plain unit generate. "decide_at_end" keeps
    // today's plain-unit path untouched.
    //
    // MECHANISM (not a field list): the staged payload is DERIVED from the
    // same `payload` the plain path sends — we spread it and override only the
    // staged-specific fields. This guarantees every Step-1 field the teacher
    // set (time, activity_type, include_sections, skill_subcomponents,
    // class_context, …) reaches the staged build, and any field added to the
    // form later flows through automatically instead of being silently dropped.
    if (isUnit && assessmentModel && assessmentModel !== "decide_at_end") {
      const unitStrandCodesForSubject = strands
        .filter((s) => s.subject === form.subject)
        .map((s) => s.code);
      const expCodes =
        multi_expectations.length > 0
          ? multi_expectations.map((e) => e.code)
          : [];
      onGenerate({
        ...payload, // same source as the plain path — forwards the whole form
        output_type: "staged_unit",
        assessment_model: assessmentModel,
        model: assessmentModel,
        strands: unitStrandCodesForSubject,
        expectations: expCodes,
        num_lessons: parseInt(form.num_lessons || "5", 10),
      });
      return;
    }
    onGenerate(payload);
  };

  const toggleSection = (key) => {
    setIncludeSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const includedCount = includeSections.size;

  const renderSelect = (label, key, options, testid) => (
    <div className="space-y-2">
      <Label
        htmlFor={testid}
        className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
      >
        {label}
      </Label>
      <Select value={form[key] || ""} onValueChange={setField(key)}>
        <SelectTrigger id={testid} data-testid={testid} className={fieldClass}>
          <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
          {form[key] ? (
            <SelectItem
              value={CLEAR}
              data-testid={`${testid}-option-clear`}
              className="text-[#4a6984]/70 italic font-medium focus:bg-[#f4f5f7] focus:text-[#4a6984]"
            >
              Select {label.toLowerCase()}
            </SelectItem>
          ) : null}
          {options.map((opt) => (
            <SelectItem
              key={opt}
              value={opt}
              data-testid={`${testid}-option-${opt.replace(/\s+/g, "-").toLowerCase()}`}
              className="text-[#4a6984] font-medium focus:bg-[#f4f5f7] focus:text-[#4a6984]"
            >
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const ModeButton = ({ value, icon: Icon, label, sub }) => {
    const active = mode === value;
    return (
      <button
        type="button"
        onClick={() => setMode(value)}
        data-testid={`mode-${value}`}
        aria-pressed={active}
        className={`flex-1 text-left rounded-lg border-2 px-4 py-3 transition-all ${
          active
            ? "border-[#1c2d3d] bg-[#1c2d3d] text-white shadow-sm"
            : "border-[#4a6984]/15 bg-white text-[#4a6984] hover:border-[#4a6984]/35"
        }`}
      >
        <div className="flex items-center gap-2 font-extrabold text-sm tracking-tight">
          <Icon className="h-4 w-4" />
          {label}
        </div>
        <p
          className={`mt-0.5 text-xs font-medium ${
            active ? "text-white/75" : "text-[#4a6984]/70"
          }`}
        >
          {sub}
        </p>
      </button>
    );
  };

  const AssessmentModelButton = ({ active, onClick, icon: Icon, label, sub, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`text-left rounded-lg border-2 px-4 py-3 transition-all ${
        active
          ? "border-[#1c2d3d] bg-[#1c2d3d] text-white shadow-sm"
          : "border-[#4a6984]/15 bg-white text-[#4a6984] hover:border-[#4a6984]/35"
      }`}
    >
      <div className="flex items-center gap-2 font-extrabold text-sm tracking-tight">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <p
        className={`mt-0.5 text-xs font-medium ${
          active ? "text-white/75" : "text-[#4a6984]/70"
        }`}
      >
        {sub}
      </p>
    </button>
  );

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="generator-form"
      className="ae-card shadow-xl shadow-[#4a6984]/5 p-6 md:p-10"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6">
        <div>
          <p className="text-[#4a6984]/70 font-bold uppercase text-xs tracking-[0.2em]">
            The Generator
          </p>
          <h2 className="font-extrabold text-2xl sm:text-3xl text-[#4a6984] mt-2">
            {isUnit
              ? "Build a multi-lesson Ontario unit plan"
              : isSummative
              ? "Design a multi-expectation summative task"
              : "Build a classroom-ready activity in seconds"}
          </h2>
        </div>
        <p className="text-[#4a6984]/70 text-sm max-w-sm">
          {isUnit
            ? "We'll outline the unit and generate each lesson for you, aligned to the skills and focus areas you choose."
            : isSummative
            ? "Pick a strand and 2+ specific expectations. We'll design a culminating task that assesses them together."
            : "Pick the parameters. We'll pair them with Ontario curriculum and Learning Skills automatically."}
        </p>
      </div>

      {/* Mode toggle — Phase 119.B: summative removed; build summatives
          from the "Assess & Report → Build a Summative" entry instead. */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8"
        data-testid="mode-toggle"
        style={hideUnitToggle ? { display: "none" } : undefined}
      >
        <ModeButton
          value="lesson"
          icon={FileText}
          label="Single lesson"
          sub="One ready-to-teach activity"
        />
        <ModeButton
          value="unit"
          icon={CalendarDays}
          label="Unit plan"
          sub="A sequence of linked lessons"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Mobile-only step header — turns the long form into a felt
            "step 1 → 2 → 3" flow without restructuring the desktop
            layout (spec #8). Shown above the essential picker grid. */}
        <div
          data-testid="generator-mobile-step-1"
          className="md:hidden col-span-1 -mb-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-black text-[#1c2d3d]"
        >
          <span className="h-5 w-5 grid place-items-center rounded-full bg-[#96ded1] text-[#1c2d3d] text-[10px] font-black">1</span>
          Pick your skill, grade & subject
        </div>
        {!isSummative && !isUnit && (
          <div className="md:col-span-1 lg:col-span-1">
            <SkillFocusPicker
              value={form.skill_focus}
              onChange={setField("skill_focus")}
              testid="select-skill-focus"
              label="Skill Focus"
            />
          </div>
        )}
        {renderSelect("Grade", "grade", gradeOptions, "select-grade")}
        {!isCombinedLesson && renderSelect("Subject", "subject", subjectOptions, "select-subject")}
        {/* 1b Stage B — unit-mode Step 1 (Strand → Big Idea → Specifics →
            Skills). Replaces the shared single-pick strand/expectation
            dropdowns for units only; lesson/summative keep those below. */}
        {isUnit && form.grade && form.subject && (
          <UnitStep1
            curriculum={curriculum}
            grade={form.grade}
            subject={form.subject}
            selectedStrandCodes={strands.filter((s) => s.subject === form.subject).map((s) => s.code)}
            onToggleStrand={(code) => dispatch({ type: "TOGGLE_STRAND", subject: form.subject, code })}
            selectedExpectationCodes={expectations.filter((e) => e.subject === form.subject).map((e) => e.code)}
            selectedExpectationDetail={expectations.filter((e) => e.subject === form.subject)}
            onToggleExpectation={(strand, code, grade) => dispatch({ type: "TOGGLE_EXPECTATION", subject: form.subject, strand, code, grade })}
            onPreselectExpectations={(list) => dispatch({ type: "PRESELECT_EXPECTATIONS", expectations: list })}
            bigIdea={bigIdea}
            onBigIdeaChange={(text, sourceCodes, suggestedCodes, source) => setBigIdea({ text, sourceCodes: sourceCodes || [], suggestedCodes: suggestedCodes || [], source: source || null })}
            skills={unitSkills}
            onToggleSkill={(name) => setUnitSkills((prev) => {
              if (prev.includes(name)) return prev.filter((n) => n !== name);
              if (prev.length >= 2) return prev;  // max two skills per unit
              return [...prev, name];
            })}
            focusStore={focusStore}
            resolveSkillId={(name) => resolveSkillId(focusStore, name)}
            unitFocusAreas={unitFocusAreas}
            onToggleFocusArea={(sid, num) => setUnitFocusAreas((prev) => {
              const cur = prev[sid] || [];
              const next = cur.includes(num) ? cur.filter((n) => n !== num) : [...cur, num];
              return { ...prev, [sid]: next };
            })}
            isSplitGrade={isSplitGrade}
            splitGrades={splitGrades}
            lookforGrade={lookforGrade}
            onLookforGrade={setLookforGrade}
            fieldClass={fieldClass}
          />
        )}
        {/* Combined Grades (lesson mode) — two-column per-grade selection
            surface. Replaces the unioned single subject/strand/expectation
            controls for a split-grade lesson. */}
        {isCombinedLesson && form.grade && (
          <CombinedGradeColumns
            curriculum={curriculum}
            splitGrades={splitGrades}
            state={state}
            dispatch={dispatch}
            expectationCap={expectationCap}
            colSubject={colSubject}
            setColSubject={setColSubject}
            linked={gradeMirrorLinked}
            setLinked={setGradeMirrorLinked}
            fieldClass={fieldClass}
          />
        )}
        {isCombinedLesson && form.grade && expectations.length > 0 && combinedMissingGrades.length > 0 && (
          <p
            data-testid="combined-grade-expectation-hint"
            className="text-[13px] font-semibold text-[#b45309] bg-[#fff7ed] border border-[#fed7aa] rounded-lg px-3 py-2 leading-relaxed"
          >
            {combinedMissingGrades.length === splitGrades.length
              ? `A combined ${splitGrades.join("/")} lesson needs at least one expectation from each grade. Please add a Grade ${splitGrades[0]} expectation and a Grade ${splitGrades[1]} expectation.`
              : `Grade ${combinedMissingGrades[0]} has no expectation selected. A combined ${splitGrades.join("/")} lesson needs at least one expectation from each grade. Please add a Grade ${combinedMissingGrades[0]} expectation.`}
          </p>
        )}
        {/* Split-grade look-for chooser for LESSONS (units have their own
            chooser inside UnitStep1). */}
        {!isUnit && isSplitGrade && (
          <div className="space-y-2" data-testid="lesson-lookfor-grade">
            <Label
              htmlFor="select-lookfor-grade"
              className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
            >
              Skill look-fors from grade
            </Label>
            <Select value={lookforGrade} onValueChange={setLookforGrade}>
              <SelectTrigger id="select-lookfor-grade" data-testid="select-lookfor-grade" className={fieldClass}>
                <SelectValue placeholder="Choose a grade" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {splitGrades.map((n) => (
                  <SelectItem key={n} value={n} data-testid={`lookfor-grade-${n}`}>
                    Grade {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#4a6984]/70">
              This lesson spans {splitGrades.join("/")}. Pick which grade&apos;s skill look-fors to use.
            </p>
          </div>
        )}
        {/* Strand — always when grade+subject have strands. Required for summative tasks. */}
        {!isUnit && !isCombinedLesson && form.grade && form.subject && strandOptions.length > 0 && (
          <div className="space-y-2">
            <Label
              htmlFor="select-strand"
              className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
            >
              Strand{" "}
              {isSummative ? (
                <span className="text-[#1c2d3d]/70 font-medium normal-case tracking-normal text-[10px]">(required)</span>
              ) : (
                <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">(optional)</span>
              )}
            </Label>
            <Select value={form.strand || ""} onValueChange={setField("strand")}>
              <SelectTrigger id="select-strand" data-testid="select-strand" className={fieldClass}>
                <SelectValue placeholder={isSummative ? "Choose a strand" : "Any strand"} />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                {form.strand ? (
                  <SelectItem
                    value={CLEAR}
                    data-testid="select-strand-option-clear"
                    className="text-[#4a6984]/70 italic font-medium focus:bg-[#f4f5f7]"
                  >
                    {isSummative ? "Choose a strand" : "Any strand"}
                  </SelectItem>
                ) : null}
                {strandOptions.map((s) => (
                  <SelectItem
                    key={s.code}
                    value={s.code}
                    data-testid={`select-strand-option-${s.code.toLowerCase()}`}
                    className="text-[#4a6984] font-medium focus:bg-[#f4f5f7]"
                  >
                    {s.code} — {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {/* Specific expectation — single-pick dropdown in lesson/unit mode.
            In summative mode the multi-select checklist below replaces it,
            so this row hides. */}
        {!isSummative && !isUnit && !isCombined && form.strand && hasCuratedSpecifics && (
          <div className="space-y-2">
            <Label
              htmlFor="select-expectation"
              className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
            >
              Specific expectation <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">(optional)</span>
            </Label>
            <Select value={form.expectation || ""} onValueChange={setField("expectation")}>
              <SelectTrigger
                id="select-expectation"
                data-testid="select-expectation"
                className={fieldClass}
              >
                <SelectValue placeholder="Any expectation in this strand" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984] max-h-80">
                {form.expectation ? (
                  <SelectItem
                    value={CLEAR}
                    data-testid="select-expectation-option-clear"
                    className="text-[#4a6984]/70 italic font-medium focus:bg-[#f4f5f7] focus:text-[#4a6984]"
                  >
                    Any expectation in this strand
                  </SelectItem>
                ) : null}
                {specificOptions.map((row) => (
                  <SelectItem
                    key={row.code}
                    value={row.code}
                    data-testid={`select-expectation-option-${row.code.toLowerCase()}`}
                    className="text-[#4a6984] font-medium focus:bg-[#f4f5f7] focus:text-[#4a6984]"
                  >
                    <span className="flex flex-col gap-0.5 py-0.5 text-left">
                      <span className="font-extrabold text-[#1c2d3d] text-[13px] tracking-tight">
                        {row.code}
                        {row.topic ? (
                          <span className="ml-2 text-[#4a6984]/70 font-semibold">
                            · {row.topic}
                          </span>
                        ) : null}
                      </span>
                      {row.description ? (
                        <span className="text-[11px] text-[#4a6984]/80 leading-snug max-w-[28rem] whitespace-normal">
                          {row.description}
                        </span>
                      ) : null}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {!isSummative && !isUnit && form.strand && !hasCuratedSpecifics && (
          <div className="space-y-2">
            <Label
              htmlFor="input-expectation"
              className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
            >
              Specific expectation <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">(optional)</span>
            </Label>
            <Input
              id="input-expectation"
              data-testid="input-expectation"
              value={form.expectation}
              onChange={(e) => setField("expectation")(e.target.value)}
              placeholder={`e.g. ${form.strand}1.4`}
              className={fieldClass}
            />
          </div>
        )}
        {isSummative
          ? renderSelect("# of class periods", "num_lessons", PERIOD_COUNT_OPTIONS, "select-num-periods")
          : isUnit
          ? (
            <div className="space-y-2 sm:col-span-2 lg:col-span-3">
              <Label
                htmlFor="input-num-lessons"
                className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
              >
                # of Lessons
              </Label>
              <p
                data-testid="num-lessons-disclaimer"
                className="text-xs text-[#4a6984]/80 leading-relaxed max-w-xl"
              >
                Every unit opens with a launch lesson — activating what students already know, gathering their questions, and introducing the culminating task and how it will be assessed. It doesn't cover new curriculum, so plan for one lesson beyond the number you enter below.
              </p>
              <Input
                id="input-num-lessons"
                data-testid="input-num-lessons"
                type="text"
                inputMode="numeric"
                value={form.num_lessons || ""}
                onChange={(e) => setField("num_lessons")(e.target.value.replace(/[^\d]/g, "").slice(0, 2))}
                placeholder="e.g. 6 (content lessons, 4–12)"
                aria-invalid={!!unitLessonError}
                className={`${fieldClass} max-w-[220px]`}
              />
              {unitLessonError ? (
                <p data-testid="num-lessons-error" className="text-xs font-semibold text-rose-600">
                  {unitLessonError}
                </p>
              ) : null}
            </div>
          )
          : renderSelect("Time", "time", TIME_OPTIONS, "select-time")}
        {isSummative
          ? renderSelect("Task format", "activity_type", SUMMATIVE_FORMAT_OPTIONS, "select-summative-format")
          : isUnit
          ? renderSelect("Unit Focus / Format", "activity_type", UNIT_FORMAT_OPTIONS, "select-unit-format")
          : renderSelect("Activity Type", "activity_type", ACTIVITY_TYPE_OPTIONS, "select-activity-type")}
        {isSummative &&
          renderSelect("Time per period", "time", TIME_OPTIONS, "select-time-per-period")}
        {isUnit &&
          renderSelect("Time per lesson", "time", TIME_OPTIONS, "select-time-per-lesson")}
      </div>

      {/* Fork Relocation (Stage B) — unit Step 2: choose the summative
          model up front. Culminating (standalone) and Ongoing (distributed)
          both run the staged build (task first, then task-aware lessons).
          "Decide at the end" keeps the plain unit + bottom-of-unit fork
          (also the fallback if a task can't be built). */}
      {isUnit && form.grade && form.subject && (
        <div className="mt-8" data-testid="unit-assessment-model">
          <p className="text-[#4a6984]/70 font-bold uppercase text-xs tracking-[0.2em] mb-1">
            How will you assess this unit?
          </p>
          <p className="text-sm text-[#4a6984]/80 mb-3">
            Pick now and we&apos;ll draft the culminating task first, then build
            lessons that intentionally lead toward it — or decide at the end.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <AssessmentModelButton
              value="standalone"
              active={assessmentModel === "standalone"}
              onClick={() => setAssessmentModel("standalone")}
              icon={Target}
              label="Culminating Task"
              sub="Students complete one focused task at the end that shows everything they've learned. Drafts the task first, then task-aware lessons."
              testid="unit-assessment-standalone"
            />
            <AssessmentModelButton
              value="distributed"
              active={assessmentModel === "distributed"}
              onClick={() => setAssessmentModel("distributed")}
              icon={Layers}
              label="Ongoing Task"
              sub="Students build one piece at a time across the unit until it all comes together. Each lesson contributes a piece."
              testid="unit-assessment-distributed"
            />
            <AssessmentModelButton
              value="decide_at_end"
              active={assessmentModel === "decide_at_end"}
              onClick={() => setAssessmentModel("decide_at_end")}
              icon={CalendarDays}
              label="Decide at the end"
              sub="Build the unit now and choose the summative (task or test) once it's ready."
              testid="unit-assessment-decide"
            />
          </div>
        </div>
      )}

      {/* Mobile-only Step 2 indicator — visually separates the optional
          details section from the essentials grid above. */}
      <div
        data-testid="generator-mobile-step-2"
        className="md:hidden mt-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-black text-[#1c2d3d]"
      >
        <span className="h-5 w-5 grid place-items-center rounded-full bg-[#96ded1] text-[#1c2d3d] text-[10px] font-black">2</span>
        Fine-tune the details (optional)
      </div>

      {/* Phase 96 — Multi-subject / multi-strand / multi-expectation
          picker. Renders once grade + primary subject are picked. */}
      <CrossCurricularPicker
        state={state}
        dispatch={dispatch}
        curriculum={curriculum}
        expectationCap={expectationCap}
        columnSubjects={isCombinedLesson ? Object.values(colSubject).filter(Boolean) : []}
      />

      {/* Phase 85a — Summative Task multi-expectation picker. Appears once
          the teacher has selected a strand that has curated specific
          expectations. Teachers must tick at least 2 codes for the
          Generate button to enable (backend enforces this too). */}
      {isSummative && form.strand && hasCuratedSpecifics && (
        <div
          data-testid="summative-expectations"
          className="mt-6 rounded-lg border border-[#1c2d3d]/15 bg-white p-5"
        >
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/80">
              Pick the expectations this task should assess
            </p>
            <span
              data-testid="summative-expectations-count"
              className={`text-[11px] font-extrabold uppercase tracking-[0.15em] ${
                summativeExpectations.length >= 2
                  ? "text-[#1c2d3d]"
                  : "text-[#b45309]"
              }`}
            >
              {summativeExpectations.length} selected · need 2+
            </span>
          </div>
          <p className="text-xs text-[#4a6984]/75 mb-4 leading-relaxed">
            A great summative task pulls together 2–4 closely related
            expectations so students can show their thinking across them
            at once.
          </p>
          <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {specificOptions.map((row) => {
              const checked = summativeExpectations.includes(row.code);
              return (
                <li
                  key={`${row.code}-${row.gradeNum || ""}`}
                  className={`rounded-md border px-3.5 py-3 transition-colors ${
                    checked
                      ? "border-[#1c2d3d] bg-[#96ded1]/15"
                      : "border-[#4a6984]/15 bg-white hover:border-[#4a6984]/35"
                  }`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSummativeExpectation(row.code)}
                      data-testid={`summative-expectation-${row.code.toLowerCase()}`}
                      className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="block font-extrabold text-sm text-[#1c2d3d] leading-tight">
                        {row.code}
                        {row.topic ? (
                          <span className="ml-2 text-[#4a6984]/70 font-semibold">
                            · {row.topic}
                          </span>
                        ) : null}
                      </span>
                      {row.description ? (
                        <span className="block text-xs text-[#4a6984]/80 mt-1 leading-relaxed">
                          {row.description}
                        </span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Phase 128 — Summative Task Designer: differentiation toggle.
          OFF (default) → ON-GRADE sheets only. ON → support + on-grade
          + extension labelled variants per supporting material. */}
      {isSummative && (
        <div
          data-testid="summative-differentiation"
          className="mt-6 rounded-lg border border-[#1c2d3d]/15 bg-white p-5"
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeDifferentiated}
              onChange={(e) => setIncludeDifferentiated(e.target.checked)}
              data-testid="summative-differentiation-toggle"
              className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
            />
            <span className="min-w-0">
              <span className="block font-extrabold text-sm text-[#1c2d3d] leading-tight">
                Include differentiated versions of student sheets
              </span>
              <span className="block text-xs text-[#4a6984]/80 mt-1 leading-relaxed">
                Off (default) — on-grade sheets only. On — also generates
                a Support version (heavier scaffolding, sentence frames)
                and an Extension version (fewer scaffolds, more depth).
                The rubric stays the same across versions.
              </span>
            </span>
          </label>
        </div>
      )}

      {/* Transferable-skill sub-component checklist (Phase 47). Appears
          immediately when a teacher picks a skill from the dropdown.
          Teacher can tick zero, one, or many — checked items are passed
          into Claude as `skill_subcomponents` and shape the WALT,
          Success Criteria, facilitation moves, and reflection. */}
      {focusAreas.length > 0 && (
        <div
          data-testid="skill-subcomponents"
          className="mt-6 rounded-lg border border-[#96ded1]/60 bg-[#96ded1]/10 p-5"
        >
          <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#4a6984]/80 mb-1">
            {form.skill_focus} — focus areas (optional)
          </p>
          <p className="text-xs text-[#4a6984]/75 mb-4 leading-relaxed">
            Tick up to two look-fors you want this lesson to build. The
            look-for, WALT, and success criterion come straight from the
            verified store. Leave blank to let Atomic Edu choose one.
          </p>
          <ul className="space-y-2">
            {focusAreas.map((lf) => {
              const checked = lookforNums.includes(lf.number);
              const disabled = !checked && lookforNums.length >= 2;
              return (
                <li
                  key={lf.number}
                  className={`rounded-md border px-3.5 py-3 transition-colors ${
                    checked
                      ? "border-[#96ded1] bg-white"
                      : disabled
                      ? "border-[#4a6984]/10 bg-white/60 opacity-60"
                      : "border-[#4a6984]/15 bg-white/70 hover:border-[#4a6984]/30"
                  }`}
                >
                  <label
                    className={`flex items-start gap-3 ${
                      disabled ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleLookfor(lf.number)}
                      data-testid={`skill-subcomponent-${lf.number}`}
                      className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="block font-extrabold text-sm text-[#1c2d3d] leading-tight">
                        {lf.name}
                      </span>
                      {lf.use_when ? (
                        <span
                          className="block text-xs text-[#4a6984]/75 mt-1 leading-relaxed italic"
                          data-testid={`skill-subcomponent-use-when-${lf.number}`}
                        >
                          {lf.use_when}
                        </span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Reading Generator (Feb 2026) — opt-in, lesson output type only.
          Adds a Student Reading + Student Response Block to the saved
          activity via a second Claude call. */}
      {!isUnit && !isSummative && (
        <div
          data-testid="reading-generator-panel"
          className="mt-6 rounded-lg border border-[#4a6984]/20 bg-[#f4f5f7]/60 p-4"
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="include-reading-checkbox"
              checked={!!form.include_reading}
              onChange={(e) =>
                setField("include_reading")(e.target.checked)
              }
              className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 accent-[#1c2d3d] shrink-0"
            />
            <span className="min-w-0">
              <span className="block text-sm font-extrabold text-[#1c2d3d]">
                Include a Reading + Response block
              </span>
              <span className="block text-xs text-[#4a6984]/85 mt-0.5 leading-relaxed">
                Adds a curriculum-aligned student reading with bolded
                vocabulary, a &ldquo;Words to Know&rdquo; list, and a 4-section
                response block (Comprehension · Thinking · Written ·
                Vocabulary Practice).
              </span>
            </span>
          </label>
          {form.include_reading && (
            <div className="mt-4 pl-7">
              <Label
                htmlFor="reading-text-type"
                className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
              >
                Text Type
              </Label>
              <select
                id="reading-text-type"
                data-testid="reading-text-type-select"
                value={form.reading_text_type || ""}
                onChange={(e) =>
                  setField("reading_text_type")(e.target.value)
                }
                className="mt-1.5 w-full bg-white border border-[#4a6984]/20 text-[#1c2d3d] font-medium rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
              >
                <option value="">— Pick a text type —</option>
                <option value="comic_strip">Comic Strip / Graphic Short</option>
                <option value="did_you_know">Did You Know?</option>
                <option value="field_notes">Field Notes</option>
                <option value="infographic">Infographic Script</option>
                <option value="interview">Interview / Q&amp;A</option>
                <option value="news_article">News / Magazine Article</option>
                <option value="opinion_piece">Opinion Piece</option>
                <option value="primary_source">Primary Source Micro-Read</option>
                <option value="short_fiction">Short Fiction</option>
              </select>
              {!form.reading_text_type && (
                <p className="mt-2 text-xs text-[#b45309]">
                  Pick a text type to enable Generate.
                </p>
              )}
              {/* Jun 2026 — multi-level reading picker. Teacher can tick
                  1, 2, or all 3 levels; each becomes its own downloadable
                  Student Reading sheet. Restricted to the 7 prose text
                  types — Comic Strip and Primary Source follow structural
                  conventions (panels / source excerpts) so they stay
                  Anchor-only and this picker is hidden for them. */}
              {form.reading_text_type &&
                !["comic_strip", "primary_source"].includes(
                  form.reading_text_type,
                ) && (
                  <div className="mt-4">
                    <Label
                      className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em] block"
                    >
                      Reading Levels
                    </Label>
                    <p className="mt-1 text-[11px] text-[#4a6984]/75">
                      Pick one or more. Each level generates as its own
                      Student Reading sheet you can download separately.
                    </p>
                    <div
                      data-testid="reading-modes-multiselect"
                      className="mt-2 flex flex-wrap gap-2"
                    >
                      {[
                        {
                          value: "anchor",
                          label: "Anchor",
                          sub: "grade-scaled (400-750 words)",
                        },
                        {
                          value: "support",
                          label: "Support",
                          sub: "shorter reading, same skill",
                        },
                        {
                          value: "enrichment",
                          label: "Enrichment",
                          sub: "longer, denser, more challenging",
                        },
                      ].map((opt) => {
                        const selected = (form.reading_modes || []).includes(
                          opt.value,
                        );
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            role="checkbox"
                            aria-checked={selected}
                            data-testid={`reading-mode-${opt.value}`}
                            onClick={() => {
                              const cur = form.reading_modes || [];
                              let next;
                              if (cur.includes(opt.value)) {
                                // Keep at least one level selected.
                                if (cur.length === 1) return;
                                next = cur.filter((m) => m !== opt.value);
                              } else {
                                next = [...cur, opt.value];
                              }
                              // Preserve canonical order.
                              const order = ["anchor", "support", "enrichment"];
                              next = order.filter((m) => next.includes(m));
                              setField("reading_modes")(next);
                            }}
                            className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs text-left transition-colors ${
                              selected
                                ? "border-[#1c2d3d]/40 bg-[#96ded1]/30 text-[#1c2d3d]"
                                : "border-[#4a6984]/20 bg-white text-[#4a6984] hover:bg-[#f4f5f7]"
                            }`}
                          >
                            <span
                              className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                selected
                                  ? "bg-[#1c2d3d] border-[#1c2d3d]"
                                  : "bg-white border-[#4a6984]/40"
                              }`}
                            >
                              {selected && (
                                <Check className="h-3 w-3 text-[#96ded1]" />
                              )}
                            </span>
                            <span>
                              <span className="block font-bold tracking-[0.05em]">
                                {opt.label}
                              </span>
                              <span className="block text-[10px] text-[#4a6984]/80 leading-tight mt-0.5">
                                {opt.sub}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {(form.reading_modes || []).includes("support") && (
                      <p className="mt-2 text-[11px] text-[#4a6984]/85">
                        Support level: ~150-350 words and a 2-question
                        multiple-choice comprehension check. Best for
                        struggling readers or shorter blocks of class
                        time.
                      </p>
                    )}
                    {(form.reading_modes || []).includes("enrichment") && (
                      <p
                        className="mt-2 text-[11px] text-[#4a6984]/85"
                        data-testid="reading-mode-enrichment-explainer"
                      >
                        Enrichment level: ~400-800 words with 2-3 stretch
                        vocabulary terms, 4-5 comprehension questions
                        including evaluation/synthesis prompts, and a
                        paragraph-length written response. Best for
                        gifted readers or extending learners who finish
                        early.
                      </p>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 space-y-2">
        <Label
          htmlFor="freeform-prompt"
          className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
        >
          Anything specific? (optional)
        </Label>
        <Textarea
          id="freeform-prompt"
          data-testid="freeform-prompt"
          rows={4}
          placeholder={
            isUnit
              ? "e.g. Coding unit for a Grade 7/8 split. High-energy class with two IEPs at Gr. 5. Use Scratch."
              : "e.g. My students struggle with peer feedback. Tie this to our novel study on Wonder."
          }
          value={form.prompt}
          onChange={(e) => setField("prompt")(e.target.value)}
          className="bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md resize-none"
        />
      </div>

      {/* What to include — appears every time so the choice is deliberate.
          Summative tasks render a fixed 5-section structure (Task, Goals
          + Criteria, Pacing, Materials, Rubric), so this picker hides. */}
      {!isSummative && (
      <div
        data-testid="sections-panel"
        className="mt-6 rounded-lg border border-[#4a6984]/20 bg-[#f4f5f7]/60"
      >
        <button
          type="button"
          onClick={() => setSectionsOpen((o) => !o)}
          data-testid="sections-panel-toggle"
          aria-expanded={sectionsOpen}
          className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="h-7 w-7 rounded-md bg-[#96ded1]/30 border border-[#96ded1]/60 grid place-items-center shrink-0">
              <ShieldCheck className="h-3.5 w-3.5 text-[#1c2d3d]" />
            </span>
            <span className="font-extrabold text-[#4a6984] text-sm tracking-tight">
              What to include in {isUnit ? "each lesson" : "this lesson"}
            </span>
            <span className="hidden sm:inline ae-pill !py-0.5 !px-2 !text-[10px]">
              {includedCount} of {OPTIONAL_SECTIONS.length}
            </span>
          </div>
          {sectionsOpen ? (
            <ChevronUp className="h-4 w-4 text-[#4a6984]/70 shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[#4a6984]/70 shrink-0" />
          )}
        </button>

        {sectionsOpen && (
          <div className="px-5 pb-5 -mt-1" data-testid="sections-panel-body">
            <p className="text-xs text-[#4a6984]/80 mb-4 leading-relaxed">
              <span className="font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                Always included:
              </span>{" "}
              {ALWAYS_INCLUDED}.
              <br />
              Pick the optional sections you want this {isUnit ? "unit's lessons" : "lesson"} to have.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {OPTIONAL_SECTIONS.map((s) => {
                const checked = includeSections.has(s.key);
                return (
                  <label
                    key={s.key}
                    data-testid={`section-${s.key}-row`}
                    className={`flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors ${
                      checked
                        ? "border-[#96ded1] bg-white"
                        : "border-[#4a6984]/15 bg-white/60 hover:border-[#4a6984]/25"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSection(s.key)}
                      data-testid={`section-${s.key}-checkbox`}
                      className="mt-1 h-4 w-4 rounded border-[#4a6984]/40 text-[#1c2d3d] focus:ring-[#96ded1]"
                    />
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-[#4a6984] leading-tight">
                        {s.label}
                      </p>
                      <p className="text-xs text-[#4a6984]/70 mt-0.5">{s.hint}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Phase 119.A/D/E — taught-lesson grounding picker for ALL modes.
          In summative mode it auto-attaches the parent unit's children
          when launched via the unit-summative bridge. In lesson/unit
          mode the teacher manually picks lessons to ground a follow-up
          activity in. */}
      <div className="mt-8 rounded-xl border border-[#4a6984]/15 bg-white px-4 py-4 sm:px-5 sm:py-5">
        <LessonGroundingPicker
          selectedIds={attachedLessonIds}
          onChange={setAttachedLessonIds}
          subjects={form.subject ? [form.subject] : (subjects || [])}
          grade={form.grade || ""}
          initialCollapsed={autoAttachedCount > 0}
          autoAttachedHint={
            autoAttachedCount > 0
              ? `${autoAttachedCount} lesson${autoAttachedCount === 1 ? "" : "s"} from this unit are auto-attached. The builder will ground the new activity in their full content.`
              : isSummative
              ? "Attach previously taught lessons so the task reflects what students actually learned."
              : "Attach previously taught lessons so this new activity echoes their examples, vocabulary, and difficulty. Optional."
          }
          emptyHint="No taught lessons in this subject yet — generate one in the Generator first."
        />
      </div>

      <div className="mt-8 rounded-xl border border-[#96ded1]/40 bg-[#96ded1]/10 px-4 py-3 sm:px-5 sm:py-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="share-to-community-checkbox"
            checked={shareToCommunity}
            onChange={(e) => setShareToCommunity(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-[#1c2d3d]/30 text-[#1c2d3d] focus:ring-2 focus:ring-[#96ded1]"
          />
          <span className="flex-1">
            <span className="block font-extrabold text-[#1c2d3d] text-sm">
              Share this lesson with the Atomic Edu community
            </span>
            <span className="block text-xs text-[#1c2d3d]/70 mt-0.5 font-medium">
              Your name, personal notes, and class details are kept private.
              Anyone on a paid plan can preview and import a copy.
            </span>
          </span>
        </label>
        {shareToCommunity && (
          <div className="mt-3 pl-7">
            <Label
              htmlFor="community-display-name"
              className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#4a6984]/70"
            >
              Display name (optional)
            </Label>
            <Input
              id="community-display-name"
              data-testid="community-display-name-input"
              value={communityDisplayName}
              onChange={(e) => setCommunityDisplayName(e.target.value.slice(0, 60))}
              placeholder={`e.g. "Ms. P" or "Grade 5 Teacher in Simcoe County"`}
              className={`${fieldClass} mt-1 max-w-md`}
              maxLength={60}
            />
            <p className="text-[10px] text-[#4a6984]/70 mt-1 font-medium">
              Leave blank to share anonymously.
            </p>
          </div>
        )}
      </div>

      {/* Mobile-only Step 3 indicator — sits right above the submit
          button so teachers know they're at the finish line. */}
      <div
        data-testid="generator-mobile-step-3"
        className="md:hidden mt-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-black text-[#1c2d3d]"
      >
        <span className="h-5 w-5 grid place-items-center rounded-full bg-[#96ded1] text-[#1c2d3d] text-[10px] font-black">3</span>
        Hit Generate
      </div>

      <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs text-[#4a6984]/60">
          {isSummative && !summativeReady ? (
            <span
              data-testid="summative-pick-hint"
              className="text-[#b45309] font-bold"
            >
              {!form.strand
                ? "Pick a strand to see expectations"
                : !hasCuratedSpecifics
                ? "This grade/subject doesn't have a curated expectation list yet — try Lesson mode."
                : "Pick 2+ expectations to enable Generate"}
            </span>
          ) : (
            "Aligned with Ontario Grades 4–8 curriculum"
          )}
        </p>
        <Button
          type="submit"
          data-testid="generate-activity-button"
          disabled={!ready || loading}
          className="ae-cta hover:ae-cta px-8 h-12 rounded-lg text-base inline-flex items-center gap-2 shadow-sm"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {isUnit
                ? assessmentModel === "standalone" || assessmentModel === "distributed"
                  ? "Planning your unit…"
                  : "Building unit…"
                : isSummative ? "Designing task…" : "Generating…"}
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              {isUnit
                ? assessmentModel === "standalone" || assessmentModel === "distributed"
                  ? "Plan this unit"
                  : "Build Unit Plan"
                : isSummative
                ? "Design Summative Task"
                : "Generate Activity"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default GeneratorForm;
